import { z } from 'zod';
import katalogData from '../data/dokumentkatalog.json' with { type: 'json' };
import { VERBINDLICHKEIT_LABEL, Verbindlichkeit } from './enums.js';
/**
 * Dokumentkatalog für die Übernahme eines Objekts.
 *
 * Dieselbe Linie wie `pruefzyklen.json`: Jeder Eintrag trägt seine Fundstelle
 * und eine Quelle, und der Katalog als Ganzes seinen Freigabestand. Es gibt
 * keine offizielle Übernahme-Checkliste — die Liste ist aus den Aufgaben des
 * Brandschutzbeauftragten (DGUV Information 205-003) und den genannten
 * Rechtsquellen abgeleitet. Solange `freigabe.status === 'entwurf'` ist, darf
 * die Oberfläche sie nicht als geprüftes Regelwerk darstellen.
 */
/**
 * Wie verbindlich die Grundlage ist — derselbe Rang wie beim Aufgabenkatalog
 * (`Verbindlichkeit` in enums.ts): Gesetz und UVV binden unmittelbar, eine
 * ASR entfaltet Vermutungswirkung, eine DIN bindet nur bei bauaufsichtlicher
 * Einführung, Zulassung oder Vertrag, „Empfehlung" ist der Rest.
 */
export const DokumentVerbindlichkeit = Verbindlichkeit;
export const DOKUMENT_VERBINDLICHKEIT_LABEL = VERBINDLICHKEIT_LABEL;
export const DokumentPflicht = z.enum(['immer', 'bedingt']);
/**
 * Warum ein Eintrag in der Checkliste steht.
 *
 * `immer` — Pflicht in jedem Objekt. `bedingt_erfuellt` — bedingt, und die
 * Bedingung ist aus dem Bestand belegt (etwa: Feuerwehrpläne, weil eine
 * Brandmeldeanlage erfasst ist). `bedingt_pruefen` — bedingt, und ob die
 * Bedingung zutrifft, weiß die Software nicht; jemand muss nachsehen und den
 * Eintrag dann entweder bearbeiten oder auf „nicht zutreffend" setzen.
 */
export const DokumentRelevanz = z.enum(['immer', 'bedingt_erfuellt', 'bedingt_pruefen']);
export const DOKUMENT_RELEVANZ_LABEL = {
    immer: 'Pflicht',
    bedingt_erfuellt: 'Bedingt — Bedingung erfüllt',
    bedingt_pruefen: 'Bedingt — bitte prüfen',
};
/** Freigabestand eines Katalogs — dieselben Werte wie bei den Prüfzyklen. */
export const KatalogFreigabeStatus = z.enum(['entwurf', 'freigegeben']);
export const katalogFreigabe = z.object({
    status: KatalogFreigabeStatus,
    durch: z.string().nullable(),
    am: z.string().nullable(),
    hinweis: z.string(),
});
export const dokumentTyp = z.object({
    /** Stabiler Schlüssel, z. B. `brandschutzordnung`. Wird in `UebernahmeDokument.katalogKey` gespeichert. */
    key: z.string(),
    titel: z.string(),
    beschreibung: z.string(),
    /** Fundstelle — nie leer. Eine Unterlage ohne Grundlage gehört nicht in den Katalog. */
    grundlage: z.string(),
    quelleUrl: z.string(),
    verbindlichkeit: DokumentVerbindlichkeit,
    pflicht: DokumentPflicht,
    /** Bei `bedingt`: unter welcher Voraussetzung die Unterlage erwartet wird. */
    bedingung: z.string().nullable(),
    /**
     * Bei `bedingt`: Anlagenart, bei deren Vorhandensein die Bedingung als
     * erfüllt gilt. `null`, wenn sich die Bedingung nicht aus dem Anlagenbestand
     * ableiten lässt (Sonderbau, Gefahrstoffe) — dann muss jemand nachsehen.
     */
    bedingungAnlagenTyp: z.string().nullable(),
    /**
     * Bei `bedingt`: Nutzungsarten, bei denen die Bedingung als erfüllt gilt.
     * Heute trägt kein Eintrag dieses Feld; es ist der Anschluss für Einträge,
     * die aus der Nutzung folgen (etwa Beherbergung), sobald eine Fundstelle
     * dafür vorliegt.
     */
    bedingungNutzungsarten: z.array(z.string()).optional(),
    /** Schlagworte für die Zuordnung hochgeladener Dateien über den Dateinamen. */
    dateinamenMuster: z.array(z.string()),
});
const raw = katalogData;
export const dokumentkatalogVersion = raw.version;
export const dokumentkatalogLastVerified = raw.lastVerified;
export const dokumentkatalogFreigabe = raw.freigabe;
/** Nachschlagen per Schlüssel — einmal beim Laden aufgebaut, nicht bei jedem Aufruf gesucht. */
const KATALOG_BY_KEY = new Map(raw.dokumente.map((d) => [d.key, d]));
/** Die Schlagworte je Eintrag, vorab kleingeschrieben — die Erkennung läuft bei jedem Upload. */
const MUSTER_BY_TYP = raw.dokumente.map((typ) => ({ typ, muster: typ.dateinamenMuster.map((m) => m.toLowerCase()) }));
export function getDokumentKatalog() {
    return raw.dokumente;
}
export function getDokumentTyp(key) {
    return KATALOG_BY_KEY.get(key) ?? null;
}
/** Relevanz eines einzelnen Katalogeintrags für ein Objekt — siehe `DokumentRelevanz`. */
export function dokumentRelevanz(typ, anlagenTypen, nutzungsarten = []) {
    if (typ.pflicht === 'immer')
        return 'immer';
    const durchAnlage = typ.bedingungAnlagenTyp !== null && anlagenTypen.includes(typ.bedingungAnlagenTyp);
    const durchNutzung = (typ.bedingungNutzungsarten ?? []).some((n) => nutzungsarten.includes(n));
    return durchAnlage || durchNutzung ? 'bedingt_erfuellt' : 'bedingt_pruefen';
}
/**
 * Welche Katalogeinträge gehören in die Checkliste eines Objekts?
 *
 * Alle — bewusst auch die bedingten, deren Bedingung nicht belegt ist. Ein
 * Explosionsschutzdokument stillschweigend wegzulassen, weil die Software
 * nichts von Gefahrstoffen weiß, wäre die falsche Richtung: Der Eintrag soll
 * sichtbar bleiben und eine Entscheidung erzwingen („nicht zutreffend" ist
 * eine). Die Rückgabe sagt nur, WARUM ein Eintrag da ist.
 */
export function relevanteDokumente(anlagenTypen, nutzungsarten = []) {
    return raw.dokumente.map((typ) => ({
        typ,
        relevanz: dokumentRelevanz(typ, anlagenTypen, nutzungsarten),
    }));
}
/**
 * Dateinamen in Wortbausteine zerlegen — Trenner sind Leerzeichen,
 * Unterstrich, Bindestrich, Punkt und Klammern.
 */
function dateinamenTokens(dateiname) {
    return dateiname
        .toLowerCase()
        .split(/[\s\-_./()[\],;]+/g)
        .filter((t) => t.length > 0);
}
/**
 * Welcher Katalogtyp passt zu diesem Dateinamen?
 *
 * Schlagwortsuche über `dateinamenMuster`: Ein Wortbaustein des Dateinamens
 * muss mit dem Muster BEGINNEN. „Wartungsprotokoll_2026" trifft so „wartung",
 * „Feuerwehrplaene_EG" trifft „feuerwehrplan" — aber „bso" trifft nicht
 * mitten in „Absorber". Bei mehreren Kandidaten gewinnt der mit dem längsten
 * getroffenen Muster, weil ein langes Schlagwort („brandschutzordnung")
 * spezifischer ist als ein kurzes („bso"). Ohne Treffer `null` — geraten wird
 * nicht.
 */
export function dokumentTypAusDateiname(dateiname) {
    const tokens = dateinamenTokens(dateiname);
    let best = null;
    for (const { typ, muster } of MUSTER_BY_TYP) {
        for (const m of muster) {
            if (!tokens.some((t) => t.startsWith(m)))
                continue;
            if (best === null || m.length > best.muster.length)
                best = { typ, muster: m };
        }
    }
    return best;
}
